/**
 * Робота з m3u8-плейлистом для зовнішніх плеєрів (VLC тощо).
 *
 * Всередині плейлиста сегменти лежать відносними шляхами (`seg_0.ts`),
 * тому окремо збережений файл ніде не відкриється. Перед завантаженням
 * підставляємо кожному рядку домен і папку самого плейлиста.
 */

const URI_ATTR = /URI="([^"]*)"/i

const withToken = (url, token) => {
  if (!token) return url
  const u = new URL(url)
  u.searchParams.set('t', token)
  return u.toString()
}

// Відносний шлях -> абсолютний URL відносно самого плейлиста
const resolveRef = (ref, playlistUrl, token) => {
  try {
    return withToken(new URL(ref, playlistUrl).toString(), token)
  } catch {
    return ref
  }
}

const normalizeUrl = (url) =>
  new URL(url, import.meta.client ? window.location.href : undefined).toString()

/**
 * Замінює всі відносні посилання в плейлисті на абсолютні.
 *
 * @param {string} text — вміст m3u8
 * @param {string} playlistUrl — абсолютний URL самого плейлиста
 * @return {string} m3u8 з абсолютними шляхами
 */
export const absolutizePlaylist = (text, playlistUrl) => {
  const token = new URL(playlistUrl).searchParams.get('t')

  return text
    .split('\n')
    .map((line) => {
      const trimmed = line.trim()
      if (!trimmed) return line

      // Теги з посиланнями всередині: EXT-X-KEY, EXT-X-MAP, EXT-X-MEDIA...
      if (trimmed.startsWith('#')) {
        const match = trimmed.match(URI_ATTR)
        if (!match?.[1]) return line
        return trimmed.replace(
          URI_ATTR,
          `URI="${resolveRef(match[1], playlistUrl, token)}"`
        )
      }

      // Сегмент або вкладений плейлист
      return resolveRef(trimmed, playlistUrl, token)
    })
    .join('\n')
}

// Прибираємо все, що не можна класти в ім'я файла
const safeFileName = (name) =>
  String(name || 'playlist')
    .replace(/[\\/:*?"<>|]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120) || 'playlist'

export const useHlsPlaylist = () => {
  /**
   * Качає m3u8 і повертає його вміст з абсолютними шляхами.
   */
  const buildPlaylist = async (hlsUrl) => {
    const url = normalizeUrl(hlsUrl)
    const res = await fetch(url)
    if (!res.ok) throw new Error(`Playlist fetch failed: ${res.status}`)
    return absolutizePlaylist(await res.text(), url)
  }

  /**
   * Зберігає плейлист файлом на диск.
   */
  const downloadPlaylist = async (hlsUrl, fileName = 'playlist') => {
    const text = await buildPlaylist(hlsUrl)
    const blob = new Blob([text], { type: 'application/vnd.apple.mpegurl' })
    const objectUrl = URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = objectUrl
    a.download = `${safeFileName(fileName)}.m3u8`
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
  }

  /**
   * Копіює пряме посилання на плейлист (його можна відкрити в VLC як мережевий потік).
   */
  const copyPlaylistUrl = async (hlsUrl) => {
    const url = normalizeUrl(hlsUrl)
    try {
      await navigator.clipboard.writeText(url)
      return true
    } catch {
      // Фолбек для незахищеного контексту / старих браузерів
      try {
        const ta = document.createElement('textarea')
        ta.value = url
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        ta.remove()
        return true
      } catch {
        return false
      }
    }
  }

  return { buildPlaylist, downloadPlaylist, copyPlaylistUrl }
}
